import styled from "styled-components";
import Refresh from "shared/components/Refresh";
import Search from "shared/components/Search";
import useStudentListView from "features/teacher/hooks/student/useStudentListView";
import { useNavigate } from "react-router-dom";
import { hw, scrollbar } from "features/homework/homeworkUi";

interface ListViewProps {
    clickedLesson: string;
}

const statusMeta = (status: string | undefined) => {
    switch (status) {
        case "suspended":
            return { label: "휴학중", color: "#D97706", bg: "#FFFBEB" };
        case "graduated":
            return { label: "퇴원", color: "#64748B", bg: "#F1F5F9" };
        default:
            return { label: "재학중", color: hw.success, bg: "#F0FDF4" };
    }
};

const StudentListView = ({ clickedLesson }: ListViewProps) => {
    const navigate = useNavigate();
    const {
        searchedLists,
        onRefreshClick,
        onStudentClick,
        search,
        onChange,
        clickedItem,
    } = useStudentListView(clickedLesson);

    return (
        <Container>
            <Header>
                <div>
                    <Title>학생 목록</Title>
                    <Sub>원생을 선택한 뒤 상담을 작성하세요.</Sub>
                </div>
                <Refresh onRefreshClick={onRefreshClick} />
            </Header>
            <Search placeholder="학생 이름을 입력해 주세요" value={search} onChange={onChange} />
            <List>
                {searchedLists.length === 0 ? (
                    <Empty>
                        <EmptyTitle>{search ? "검색 결과가 없습니다" : "배정된 학생이 없습니다"}</EmptyTitle>
                        <EmptyText>
                            {search ? "다른 이름으로 다시 검색해 보세요." : "이 학급에 배정된 원생이 없습니다."}
                        </EmptyText>
                    </Empty>
                ) : (
                    searchedLists.map((item) => {
                        const status = statusMeta(item.status);
                        const selected = item.uid === clickedItem;
                        return (
                            <Item
                                key={item.uid}
                                $on={selected}
                                onClick={() => onStudentClick(item.uid)}
                            >
                                <StudentName>{item.name}</StudentName>
                                <StatusBadge $color={status.color} $bg={status.bg}>
                                    {status.label}
                                </StatusBadge>
                            </Item>
                        );
                    })
                )}
            </List>
            <Buttons>
                {clickedItem ? (
                    <CounselButton type="button" onClick={() => navigate("register", { state: clickedItem })}>
                        상담작성
                    </CounselButton>
                ) : (
                    <Hint>학생을 선택하면 상담을 작성할 수 있습니다.</Hint>
                )}
            </Buttons>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-width: 0;
    min-height: 0;
    height: 100%;
    padding: 20px 18px 16px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${hw.border};
    flex-shrink: 0;
`;

const Title = styled.h2`
    margin: 0;
    color: ${hw.text};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.02em;
`;

const Sub = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${hw.textMuted};
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    border-radius: 12px;
    border: 1px solid ${hw.border};
    background: ${hw.surface};
    overflow-y: auto;
    overflow-x: hidden;
    ${scrollbar}
`;

const Item = styled.div<{ $on: boolean }>`
    padding: 12px 14px;
    border-bottom: 1px solid ${hw.border};
    background: ${props => props.$on ? hw.brandSoft : hw.surface};
    box-shadow: ${props => props.$on ? `inset 3px 0 0 ${hw.brand}` : "none"};
    color: ${hw.text};
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 10px;

    &:hover {
        background: ${props => props.$on ? hw.brandSoft : hw.bg};
    }

    &:last-child {
        border-bottom: none;
    }
`;

const StudentName = styled.span`
    flex: 1;
    min-width: 0;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const StatusBadge = styled.span<{ $color: string; $bg: string }>`
    display: inline-flex;
    padding: 3px 8px;
    border-radius: 999px;
    background: ${props => props.$bg};
    color: ${props => props.$color};
    font-weight: 700;
    font-size: 11px;
    flex-shrink: 0;
`;

const Empty = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 36px 16px;
    text-align: center;
`;

const EmptyTitle = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: ${hw.text};
`;

const EmptyText = styled.div`
    font-size: 13px;
    color: ${hw.textMuted};
    line-height: 1.5;
`;

const Buttons = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid ${hw.border};
    flex-shrink: 0;
    min-height: 44px;
`;

const Hint = styled.span`
    font-size: 13px;
    color: ${hw.textSubtle};
`;

const CounselButton = styled.button`
    appearance: none;
    padding: 9px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    background: ${hw.brand};
    color: white;

    &:hover {
        background: ${hw.brandDark};
    }
`;

export default StudentListView;
